import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap, type Schemas } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { errorMessage } from '../lib/notify';
import { QuickCustomerDialog } from './QuickCustomerDialog';

export type Contact = Schemas['ContactList']['data'][number];
export type Variant = Schemas['VariantList']['data'][number];

/** The "Create “name”" / "New customer" entry at the end of the list. */
interface CreateOption {
  create: string;
}
const isCreate = (o: Contact | CreateOption): o is CreateOption => 'create' in o;
/** Typed text that is a phone number goes into the new customer's phone, not the name. */
const looksLikePhone = (v: string) => /^[+\d][\d\s-]{5,}$/.test(v);

/**
 * Searchable customer/supplier picker (server-side search on /contacts). New contacts can be added
 * from the list (also offline on the desktop): customers always (when the user may create them),
 * through a small name + phone form, and suppliers with `allowCreate`, by name. The new contact is
 * selected straight away.
 */
export function ContactPicker({
  type,
  value,
  onChange,
  label,
  error,
  helperText,
  allowCreate = false,
}: {
  type: 'customer' | 'supplier';
  value: Contact | null;
  onChange: (contact: Contact | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  allowCreate?: boolean;
}) {
  const [q, setQ] = useState('');
  const [newCustomer, setNewCustomer] = useState<{ name: string; phone: string } | null>(null);
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ['contacts', 'picker', type, q],
    queryFn: () =>
      unwrap(
        api.GET('/contacts', {
          params: { query: { type, q: q || undefined, pageSize: 20, isActive: 'true' } },
        }),
      ),
    placeholderData: (prev) => prev,
  });
  const create = useMutation({
    mutationFn: (name: string) =>
      unwrap(
        api.POST('/contacts', {
          params: { header: { 'idempotency-key': crypto.randomUUID() } },
          body: { name, isCustomer: type === 'customer', isSupplier: type === 'supplier' },
        }),
      ),
    onSuccess: async (created) => {
      onChange(created);
      setQ('');
      await qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  const name = q.trim();
  const contacts = data?.data ?? [];
  const isCustomer = type === 'customer';
  const offerCreate =
    (isCustomer || allowCreate) &&
    can(`${type}s.create`) &&
    (isCustomer || name.length > 0) &&
    !isFetching &&
    !contacts.some((c) => c.name.toLowerCase() === name.toLowerCase());
  const options: (Contact | CreateOption)[] = offerCreate
    ? [...contacts, { create: name }]
    : contacts;
  return (
    <>
      <Autocomplete<Contact | CreateOption>
        value={value}
        onChange={(_, v) => {
          if (v && isCreate(v)) {
            if (!isCustomer) create.mutate(v.create);
            else if (looksLikePhone(v.create)) setNewCustomer({ name: '', phone: v.create });
            else setNewCustomer({ name: v.create, phone: '' });
          } else onChange(v);
        }}
        onInputChange={(_, v, reason) => reason === 'input' && setQ(v)}
        options={options}
        loading={isFetching || create.isPending}
        disabled={create.isPending}
        filterOptions={(x) => x}
        getOptionLabel={(c) => (isCreate(c) ? c.create : c.name)}
        noOptionsText={isFetching ? 'Searching…' : `No ${type}s found`}
        isOptionEqualToValue={(a, b) => (!isCreate(a) && !isCreate(b) ? a.id === b.id : a === b)}
        renderOption={({ key, ...props }, c) =>
          isCreate(c) ? (
            <Box
              component="li"
              key={key}
              {...props}
              sx={{ gap: 1, color: 'primary.main', borderTop: 1, borderColor: 'divider' }}
            >
              <PersonAddAlt1Rounded fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {c.create ? `Create “${c.create}”` : `New ${type}`}
              </Typography>
            </Box>
          ) : (
            <Box component="li" key={key} {...props}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[c.phone, c.email].filter(Boolean).join(' · ') || (c.isWalkIn ? 'Walk-in' : '')}
                </Typography>
              </Box>
            </Box>
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? (type === 'customer' ? 'Customer' : 'Supplier')}
            error={error || create.isError}
            helperText={
              create.isPending
                ? `Adding ${type}…`
                : create.isError
                  ? errorMessage(create.error)
                  : helperText
            }
          />
        )}
      />
      <QuickCustomerDialog
        open={newCustomer !== null}
        initialName={newCustomer?.name}
        initialPhone={newCustomer?.phone}
        onClose={() => setNewCustomer(null)}
        onCreated={(contact) => {
          setNewCustomer(null);
          setQ('');
          onChange(contact);
        }}
      />
    </>
  );
}

/** Searchable product variant picker (name, SKU or barcode on /variants). */
export function VariantPicker({
  value,
  onChange,
  label = 'Product',
  autoFocus,
}: {
  value: Variant | null;
  onChange: (variant: Variant | null) => void;
  label?: string;
  autoFocus?: boolean;
}) {
  const [q, setQ] = useState('');
  const { data, isFetching } = useQuery({
    queryKey: ['variants', 'picker', q],
    queryFn: () =>
      unwrap(api.GET('/variants', { params: { query: { q: q || undefined, pageSize: 20 } } })),
    placeholderData: (prev) => prev,
  });
  const describe = (v: Variant) => {
    const r = v as unknown as Record<string, unknown>;
    const name =
      typeof r.displayName === 'string' ? r.displayName : typeof r.name === 'string' ? r.name : '';
    const sku = typeof r.sku === 'string' ? r.sku : '';
    return { name: name || sku, sku };
  };
  return (
    <Autocomplete
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v, reason) => reason === 'input' && setQ(v)}
      options={data?.data ?? []}
      loading={isFetching}
      filterOptions={(x) => x}
      getOptionLabel={(v) => describe(v).name}
      isOptionEqualToValue={(a, b) => (a as { id: string }).id === (b as { id: string }).id}
      renderOption={({ key, ...props }, v) => (
        <Box component="li" key={key} {...props}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {describe(v).name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {describe(v).sku}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} autoFocus={autoFocus} />}
    />
  );
}
